import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Public Pages
import LandingPage from "./pages/landing/LandingPage";
import LoginPage from "./pages/LoginPage";

// Protected Pages
import OwnerDashboard from "./pages/owner/OwnerDashboard";
import CompanyDeepDive from "./pages/owner/CompanyDeepDive";
import CompanyManagement from "./pages/owner/CompanyManagement";
import ApprovalInbox from "./pages/owner/ApprovalInbox";
import AdminDashboard from "./pages/admin/AdminDashboard";
import EmployeeManagement from "./pages/admin/EmployeeManagement";
import ShiftManagement from "./pages/admin/ShiftManagement";
import LeaveTypesManagement from "./pages/admin/LeaveTypesManagement";
import PayrollManagement from "./pages/admin/PayrollManagement";
import FeatureFlagsManagement from "./pages/admin/FeatureFlagsManagement";
import ReportsPage from "./pages/admin/ReportsPage";
import ShiftAssignmentPage from "./pages/admin/ShiftAssignmentPage";
import HolidayManagement from "./pages/admin/HolidayManagement";
import BrandingSettings from "./pages/admin/BrandingSettings";
import ManagerDashboard from "./pages/manager/ManagerDashboard";
import ApprovalQueue from "./pages/manager/ApprovalQueue";
import MyTeamPage from "./pages/manager/MyTeamPage";
import TeamAttendancePage from "./pages/manager/TeamAttendancePage";
import TeamReportsPage from "./pages/manager/TeamReportsPage";
import EmployeeDashboard from "./pages/employee/EmployeeDashboard";
import MyAttendance from "./pages/employee/MyAttendance";
import MyLeaves from "./pages/employee/MyLeaves";
import MyPayslips from "./pages/employee/MyPayslips";
import MyTasksPage from "./pages/employee/MyTasksPage";
import MyRequestsPage from "./pages/employee/MyRequestsPage";

// HRIS Pages
import EmployeeProfile from "./pages/hris/EmployeeProfile";
import DocumentUpload from "./pages/hris/DocumentUpload";

// Protected Route Component - only for /app/* routes
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect based on role within /app/*
    switch (user?.role) {
      case 'owner':
        return <Navigate to="/app/owner" replace />;
      case 'admin':
        return <Navigate to="/app/admin" replace />;
      case 'manager':
        return <Navigate to="/app/manager" replace />;
      default:
        return <Navigate to="/app/employee" replace />;
    }
  }
  
  return children;
};

// Role-based redirect within app
const AppRoleRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  switch (user?.role) {
    case 'owner':
      return <Navigate to="/app/owner" replace />;
    case 'admin':
      return <Navigate to="/app/admin" replace />;
    case 'manager':
      return <Navigate to="/app/manager" replace />;
    default:
      return <Navigate to="/app/employee" replace />;
  }
};

// Login page with redirect if authenticated
const LoginPageWrapper = () => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated && user) {
    const dashboardPath = {
      owner: '/app/owner',
      admin: '/app/admin',
      manager: '/app/manager',
      employee: '/app/employee'
    }[user.role] || '/app/employee';
    return <Navigate to={dashboardPath} replace />;
  }

  return <LoginPage />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* ==================== PUBLIC ROUTES ==================== */}
      
      {/* Landing page - public root */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Login page */}
      <Route path="/login" element={<LoginPageWrapper />} />
      
      {/* ==================== APP ROUTES (Auth Protected) ==================== */}
      
      {/* Default /app redirect based on role */}
      <Route path="/app" element={<AppRoleRedirect />} />
      
      {/* Owner routes */}
      <Route 
        path="/app/owner" 
        element={
          <ProtectedRoute allowedRoles={['owner']}>
            <OwnerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/owner/companies" 
        element={
          <ProtectedRoute allowedRoles={['owner']}>
            <CompanyManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/owner/approval-inbox" 
        element={
          <ProtectedRoute allowedRoles={['owner']}>
            <ApprovalInbox />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/owner/company/:companyId" 
        element={
          <ProtectedRoute allowedRoles={['owner']}>
            <CompanyDeepDive />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin routes */}
      <Route 
        path="/app/admin" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/admin/employees" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <EmployeeManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/admin/shifts" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <ShiftManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/admin/leave-types" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <LeaveTypesManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/admin/payroll" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <PayrollManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/admin/features" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <FeatureFlagsManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/admin/reports" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <ReportsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/admin/shift-assignments" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <ShiftAssignmentPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/admin/holidays" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <HolidayManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/admin/branding" 
        element={
          <ProtectedRoute allowedRoles={['owner', 'admin']}>
            <BrandingSettings />
          </ProtectedRoute>
        } 
      />
      
      {/* HRIS routes */}
      <Route 
        path="/app/hris/employees/:employeeId" 
        element={
          <ProtectedRoute allowedRoles={['owner', 'admin', 'manager', 'employee']}>
            <EmployeeProfile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/hris/employees/:employeeId/documents/upload" 
        element={
          <ProtectedRoute allowedRoles={['owner', 'admin']}>
            <DocumentUpload />
          </ProtectedRoute>
        } 
      />
      
      {/* Manager routes */}
      <Route 
        path="/app/manager" 
        element={
          <ProtectedRoute allowedRoles={['manager']}>
            <ManagerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/manager/approvals" 
        element={
          <ProtectedRoute allowedRoles={['manager']}>
            <ApprovalQueue />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/manager/team" 
        element={
          <ProtectedRoute allowedRoles={['manager', 'admin']}>
            <MyTeamPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/manager/attendance" 
        element={
          <ProtectedRoute allowedRoles={['manager']}>
            <TeamAttendancePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/manager/reports" 
        element={
          <ProtectedRoute allowedRoles={['manager']}>
            <TeamReportsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Employee routes */}
      <Route 
        path="/app/employee" 
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'admin']}>
            <EmployeeDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/employee/attendance" 
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'admin']}>
            <MyAttendance />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/employee/leaves" 
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'admin']}>
            <MyLeaves />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/employee/payslips" 
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'admin']}>
            <MyPayslips />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/employee/tasks" 
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'admin']}>
            <MyTasksPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/app/employee/requests" 
        element={
          <ProtectedRoute allowedRoles={['employee', 'manager', 'admin']}>
            <MyRequestsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* ==================== LEGACY REDIRECTS ==================== */}
      {/* Redirect old routes to new /app/* structure */}
      <Route path="/owner/*" element={<Navigate to="/app/owner" replace />} />
      <Route path="/admin/*" element={<Navigate to="/app/admin" replace />} />
      <Route path="/manager/*" element={<Navigate to="/app/manager" replace />} />
      <Route path="/employee/*" element={<Navigate to="/app/employee" replace />} />
      
      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
